import { useCallback, useEffect, useRef } from 'react';

import { toFailureReason } from '../failureReason';
import { AI_TOOLKIT_OPERATIONS, VideoEditPollResponse, VideoEditSubmitAction, VideoEditSubmitState } from '../types';
import { PollResult, useGenerationPolling } from './useGenerationPolling';

export type UseVideoEditPollingProps = {
    videoEditSubmitState: VideoEditSubmitState;
    dispatchVideoEditSubmit: (action: VideoEditSubmitAction) => void;
    refreshCredits: () => Promise<number>;
};

export const useVideoEditPolling = ({
    videoEditSubmitState,
    dispatchVideoEditSubmit,
    refreshCredits,
}: UseVideoEditPollingProps): void => {
    const videoEditStateRef = useRef<VideoEditSubmitState>(videoEditSubmitState);
    useEffect(() => {
        videoEditStateRef.current = videoEditSubmitState;
    });

    const handleVideoEditPoll = useCallback(
        async (taskId: string): Promise<PollResult> => {
            const endpoint =
                videoEditStateRef.current.operation === AI_TOOLKIT_OPERATIONS.UPSCALE
                    ? '/api/upscale'
                    : '/api/video-edit';
            const res = await fetch(`${endpoint}/${encodeURIComponent(taskId)}`);
            const payload = (await res.json()) as VideoEditPollResponse;

            if (payload.status === 'SUCCEEDED' && payload.videoUrl) {
                const { aiContentId, lastAiContentId, aiContentType, aiModelName } = videoEditStateRef.current;
                dispatchVideoEditSubmit({
                    type: 'GENERATION_SUCCESS',
                    editedVideoUrl: payload.videoUrl,
                    aiContentId,
                    lastAiContentId,
                    aiContentType,
                    aiModelName,
                });
                return 'success';
            }

            if (payload.status === 'FAILED' || payload.status === 'CANCELLED') {
                void refreshCredits();
                dispatchVideoEditSubmit({
                    type: 'GENERATION_ERROR',
                    failureReason: toFailureReason(payload.failureReason) ?? 'generation_failed',
                });
                return 'error';
            }

            return 'continue';
        },
        [dispatchVideoEditSubmit, refreshCredits],
    );

    useGenerationPolling({
        taskId: videoEditSubmitState.pendingTaskId,
        status: videoEditSubmitState.status,
        onPoll: handleVideoEditPoll,
        onTimeout: () => {
            void refreshCredits();
            dispatchVideoEditSubmit({ type: 'GENERATION_ERROR' });
        },
    });
};
